import React from "react";

import { useTranslation } from "@refinedev/core";

import { InputLabel, Stack, Typography } from "@mui/material";

/**
 * Wrapper component that combines a label with an input field.
 * Provides consistent spacing and label styling across all form fields.
 *
 * @example
 * <FormField label="First Name" required>
 *   <TextField value={firstName} onChange={handleChange} />
 * </FormField>
 *
 * @param label - Optional label text
 * @param required - Whether to show required indicator (*)
 * @param showOptionalTag - If form field is not required, show optional tag next to label (default: false)
 * @param children - The input component (TextField, Select, etc.)
 */

interface FormFieldProps {
  label?: string;
  required?: boolean;
  showOptionalTag?: boolean;
  children: React.ReactNode;
}

export const FormField: React.FC<FormFieldProps> = ({ label, required, children, showOptionalTag = false }) => {
  const { translate } = useTranslation();

  return (
    <Stack direction="column" gap={1}>
      {label && (
        <InputLabel required={required}>
          {label}
          {!required && showOptionalTag && (
            <Typography color="text.secondary" component="span" variant="caption" sx={{ ml: 1 }}>
              {translate("common.optional")}
            </Typography>
          )}
        </InputLabel>
      )}
      {children}
    </Stack>
  );
};
